import { useEffect, useState } from "react";
import { FaStar, FaRegStar } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import api from "../../../configs/api";
import { showError, showSuccess } from "../../../utils/toastUtils";

const INITIAL_FORM = {
  name: "",
  description: "",
  rating: 0,
  status: "active",
};

/* Interactive Star Picker */
const StarPicker = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => {
        const starVal = i + 1;
        const filled = starVal <= (hovered || value);
        return (
          <button
            key={i}
            type="button"
            onClick={() => onChange(starVal)}
            onMouseEnter={() => setHovered(starVal)}
            onMouseLeave={() => setHovered(0)}
            className="transition-transform hover:scale-110"
          >
            {filled ? (
              <FaStar size={22} className="text-yellow-400" />
            ) : (
              <FaRegStar size={22} className="text-yellow-300/50" />
            )}
          </button>
        );
      })}
      {value > 0 && (
        <span className="ml-2 text-xs text-descriptionText">
          {value === 1 && "Poor"}
          {value === 2 && "Fair"}
          {value === 3 && "Good"}
          {value === 4 && "Very Good"}
          {value === 5 && "Excellent"}
        </span>
      )}
    </div>
  );
};

const CreateReviewModal = ({ modal, onReviewCreated }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const isOpen = modal?.isOpen && modal?.mode === "create";

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setForm(INITIAL_FORM);
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.description.trim()) e.description = "Description is required";
    if (form.rating === 0) e.rating = "Please select a rating";
    return e;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSubmitting(true);
      const res = await api.post("/reviews", form);
      onReviewCreated?.(res.data.data);
      showSuccess("Review created successfully");
      modal.close();
    } catch (error) {
      console.error(error);
      showError(error?.response?.data?.message || "Failed to create review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* BACKDROP */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
        onClick={modal.close}
      />

      {/* MODAL */}
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div className="w-full max-w-lg bg-white border border-borderLight shadow-2xl">
          {/* HEADER */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-borderLight">
            <h2 className="text-sm font-semibold text-headingText tracking-wide">
              Create Review
            </h2>
            <button
              onClick={modal.close}
              className="w-7 h-7 flex items-center justify-center text-descriptionText hover:text-headingText hover:bg-divider transition"
            >
              <IoClose size={18} />
            </button>
          </div>

          {/* BODY */}
          <div className="px-5 py-5 space-y-4">
            {/* NAME */}
            <div>
              <label className="block text-xs font-medium text-headingText mb-1.5">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Reviewer name"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className={`w-full h-[40px] px-3 border text-sm outline-none focus:border-primaryAccent bg-white transition-colors ${
                  errors.name ? "border-red-400" : "border-borderLight"
                }`}
              />
              {errors.name && (
                <p className="text-xs text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            {/* RATING */}
            <div>
              <label className="block text-xs font-medium text-headingText mb-1.5">
                Rating <span className="text-red-500">*</span>
              </label>
              <StarPicker
                value={form.rating}
                onChange={(val) => handleChange("rating", val)}
              />
              {errors.rating && (
                <p className="text-xs text-red-500 mt-1">{errors.rating}</p>
              )}
            </div>

            {/* DESCRIPTION */}
            <div>
              <label className="block text-xs font-medium text-headingText mb-1.5">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={4}
                placeholder="Write the review..."
                value={form.description}
                onChange={(e) => handleChange("description", e.target.value)}
                className={`w-full px-3 py-2 border text-sm outline-none focus:border-primaryAccent bg-white transition-colors resize-none ${
                  errors.description ? "border-red-400" : "border-borderLight"
                }`}
              />
              {errors.description && (
                <p className="text-xs text-red-500 mt-1">{errors.description}</p>
              )}
            </div>

            {/* STATUS */}
            <div>
              <label className="block text-xs font-medium text-headingText mb-1.5">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) => handleChange("status", e.target.value)}
                className="w-full h-[40px] px-3 border border-borderLight text-sm outline-none focus:border-primaryAccent bg-white transition-colors"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* FOOTER */}
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-borderLight bg-surfaceColor">
            <button
              onClick={modal.close}
              className="h-[38px] px-4 border border-borderLight text-sm text-descriptionText hover:text-headingText hover:bg-divider transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="h-[38px] px-5 bg-btnColor text-white text-sm font-medium hover:bg-btnHoverColor transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Creating..." : "Create Review"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateReviewModal;